import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type DragEvent,
    type KeyboardEvent,
} from 'react'
import { cn } from '@/common/lib/cn/cn'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { Button } from '@/common/ui/Form/Button'
import { Modal } from '@/common/ui/Modal'
import { Icon } from '../../Icon'

export type GalleryImageItem = string

type BaseProps<T extends FieldValues> = {
    control: Control<T>
    name: FieldPath<T>
    disabled?: boolean
    galleryImages: GalleryImageItem[]
    className?: string
    title?: string
    emptyText?: string
    autoSelectFirst?: boolean
    onConvertError?: (error: unknown) => void
}

type SingleProps = {
    multiple?: false
    maxFiles?: never
}

type MultipleProps = {
    multiple: true
    maxFiles?: number
}

export type SelectImageFromGalleryProps<T extends FieldValues> = BaseProps<T> & (SingleProps | MultipleProps)

type CacheEntry = {
    previewUrl: string
    sourceUrl: string | null
}

type SelectedItem = {
    key: string
    file: File
    sourceUrl: string | null
    previewUrl: string
}

const isFileValue = (value: unknown): value is File => {
    return typeof File !== 'undefined' && value instanceof File
}

const buildFileKey = (file: File, sourceUrl: string | null): string => {
    if (sourceUrl) {
        return `gallery:${sourceUrl}`
    }
    return `file:${file.name}:${file.size}:${file.lastModified}`
}

const createFileFromGalleryImage = async (galleryImage: GalleryImageItem): Promise<File> => {
    const response = await fetch(galleryImage)
    if (!response.ok) {
        throw new Error('Не удалось получить изображение из галереи')
    }

    const blob = await response.blob()
    const fileName = galleryImage.split('/').pop()?.split('?')[0]
    return new File([blob], fileName || 'image.jpg', {
        type: blob.type || 'image/jpeg',
    })
}

const setModalOpen = (modalId: string, open: boolean) => {
    const el = document.getElementById(modalId) as HTMLInputElement | null
    if (el) {
        el.checked = open
    }
}

export const SelectImageFromGallery = <T extends FieldValues>({
    control,
    disabled = false,
    name,
    galleryImages,
    className,
    title = 'Исходное изображение',
    emptyText = 'Фотографии в галерее отсутствуют',
    multiple = false,
    autoSelectFirst = !multiple,
    onConvertError,
    maxFiles,
}: SelectImageFromGalleryProps<T>) => {
    const { field } = useController({ control, name })
    const rawId = useId()
    const modalId = useMemo(() => `select-image-gallery-${rawId.replace(/:/g, '')}`, [rawId])
    const [isSelecting, setIsSelecting] = useState(false)
    const [isDragOverUploadArea, setIsDragOverUploadArea] = useState(false)
    const uploadInputRef = useRef<HTMLInputElement | null>(null)
    const failedAutoSelectImageUrlRef = useRef<string | null>(null)
    const autoSelectDoneRef = useRef(false)
    const cacheRef = useRef<Map<File, CacheEntry>>(new Map())

    const files = useMemo<File[]>(() => {
        const value = field.value
        if (multiple) {
            return Array.isArray(value) ? value.filter(isFileValue) : []
        }
        return isFileValue(value) ? [value] : []
    }, [field.value, multiple])

    const items = useMemo<SelectedItem[]>(() => {
        const cache = cacheRef.current
        const fileSet = new Set(files)
        for (const [cachedFile, entry] of cache) {
            if (!fileSet.has(cachedFile)) {
                URL.revokeObjectURL(entry.previewUrl)
                cache.delete(cachedFile)
            }
        }

        return files.map(file => {
            let entry = cache.get(file)
            if (!entry) {
                entry = { previewUrl: URL.createObjectURL(file), sourceUrl: null }
                cache.set(file, entry)
            }
            return {
                key: buildFileKey(file, entry.sourceUrl),
                file,
                sourceUrl: entry.sourceUrl,
                previewUrl: entry.previewUrl,
            }
        })
    }, [files])

    useEffect(() => {
        const cache = cacheRef.current
        return () => {
            cache.forEach(entry => URL.revokeObjectURL(entry.previewUrl))
            cache.clear()
        }
    }, [])

    const emitFieldChange = useCallback(
        (nextFiles: File[]) => {
            if (multiple) {
                field.onChange(nextFiles)
                return
            }
            field.onChange(nextFiles[0] ?? null)
        },
        [field, multiple]
    )

    const getCurrentFiles = useCallback((): File[] => {
        const value = field.value
        if (multiple) {
            return Array.isArray(value) ? value.filter(isFileValue) : []
        }
        return isFileValue(value) ? [value] : []
    }, [field.value, multiple])

    const closeModal = useCallback(() => {
        setModalOpen(modalId, false)
    }, [modalId])

    const openModal = useCallback(() => {
        setModalOpen(modalId, true)
    }, [modalId])

    const appendFiles = useCallback(
        (newFiles: File[]) => {
            if (newFiles.length === 0) {
                return
            }

            const validFiles = newFiles.filter(file => {
                if (!file.type.startsWith('image/')) {
                    onConvertError?.(new Error('Можно загрузить только изображение'))
                    return false
                }
                return true
            })

            if (validFiles.length === 0) {
                return
            }

            autoSelectDoneRef.current = true
            const current = getCurrentFiles()

            if (!multiple) {
                const firstFile = validFiles[0]
                if (!firstFile) {
                    return
                }
                emitFieldChange([firstFile])
                closeModal()
                return
            }

            const currentKeys = new Set(
                current.map(file => {
                    const entry = cacheRef.current.get(file)
                    return buildFileKey(file, entry?.sourceUrl ?? null)
                })
            )
            const availableSlots =
                typeof maxFiles === 'number' ? Math.max(0, maxFiles - current.length) : validFiles.length
            const filesToAdd: File[] = []
            for (const file of validFiles) {
                if (filesToAdd.length >= availableSlots) {
                    break
                }
                const key = buildFileKey(file, null)
                if (currentKeys.has(key)) {
                    continue
                }
                currentKeys.add(key)
                filesToAdd.push(file)
            }

            if (filesToAdd.length === 0) {
                return
            }

            emitFieldChange([...current, ...filesToAdd])
        },
        [closeModal, emitFieldChange, getCurrentFiles, maxFiles, multiple, onConvertError]
    )

    const toggleGalleryImage = useCallback(
        async (galleryImage: GalleryImageItem) => {
            const key = `gallery:${galleryImage}`
            const current = getCurrentFiles()
            const existingFile = current.find(file => {
                const entry = cacheRef.current.get(file)
                return entry?.sourceUrl === galleryImage || buildFileKey(file, entry?.sourceUrl ?? null) === key
            })

            if (existingFile) {
                autoSelectDoneRef.current = true
                emitFieldChange(current.filter(file => file !== existingFile))
                return
            }

            if (multiple && typeof maxFiles === 'number' && current.length >= maxFiles) {
                onConvertError?.(new Error(`Можно выбрать не более ${maxFiles} изображений`))
                return
            }

            autoSelectDoneRef.current = true
            setIsSelecting(true)

            try {
                const file = await createFileFromGalleryImage(galleryImage)
                cacheRef.current.set(file, {
                    previewUrl: URL.createObjectURL(file),
                    sourceUrl: galleryImage,
                })

                if (!multiple) {
                    emitFieldChange([file])
                    closeModal()
                    return
                }

                emitFieldChange([...getCurrentFiles(), file])
            } catch (error) {
                onConvertError?.(error)
            } finally {
                setIsSelecting(false)
            }
        },
        [closeModal, emitFieldChange, getCurrentFiles, maxFiles, multiple, onConvertError]
    )

    const removeFile = useCallback(
        (fileToRemove: File) => {
            autoSelectDoneRef.current = true
            const current = getCurrentFiles()
            emitFieldChange(current.filter(file => file !== fileToRemove))
        },
        [emitFieldChange, getCurrentFiles]
    )

    const clearAll = useCallback(() => {
        autoSelectDoneRef.current = true
        emitFieldChange([])
    }, [emitFieldChange])

    useEffect(() => {
        if (!autoSelectFirst || autoSelectDoneRef.current) {
            return
        }
        if (files.length > 0 || isSelecting) {
            return
        }
        if (multiple && typeof maxFiles === 'number' && maxFiles <= 0) {
            return
        }

        const firstGalleryImage = galleryImages[0]
        if (!firstGalleryImage) {
            return
        }

        if (failedAutoSelectImageUrlRef.current === firstGalleryImage) {
            return
        }

        autoSelectDoneRef.current = true
        setIsSelecting(true)

        const handleAutoSelect = async () => {
            try {
                const file = await createFileFromGalleryImage(firstGalleryImage)
                cacheRef.current.set(file, {
                    previewUrl: URL.createObjectURL(file),
                    sourceUrl: firstGalleryImage,
                })
                emitFieldChange([file])
            } catch (error) {
                failedAutoSelectImageUrlRef.current = firstGalleryImage
                onConvertError?.(error)
            } finally {
                setIsSelecting(false)
            }
        }

        void handleAutoSelect()
    }, [autoSelectFirst, emitFieldChange, files.length, galleryImages, isSelecting, maxFiles, multiple, onConvertError])

    const handleOpenGallery = () => {
        if (isSelecting || disabled) {
            return
        }

        openModal()
    }

    const handleUploadInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files ?? [])
        appendFiles(selectedFiles)
        event.target.value = ''
    }

    const handleUploadAreaClick = () => {
        if (isSelecting) {
            return
        }

        uploadInputRef.current?.click()
    }

    const handleUploadAreaKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return
        }

        event.preventDefault()
        handleUploadAreaClick()
    }

    const handleUploadAreaDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (isSelecting) {
            return
        }

        setIsDragOverUploadArea(true)
    }

    const handleUploadAreaDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragOverUploadArea(false)
    }

    const handleUploadAreaDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragOverUploadArea(false)
        if (isSelecting) {
            return
        }

        const droppedFiles = Array.from(event.dataTransfer.files ?? [])
        appendFiles(droppedFiles)
    }

    const selectedGalleryUrls = useMemo(() => {
        const set = new Set<string>()
        for (const item of items) {
            if (item.sourceUrl) {
                set.add(item.sourceUrl)
            }
        }
        return set
    }, [items])

    const uploadedItems = useMemo(() => items.filter(item => item.sourceUrl === null), [items])
    const canAddMore = !multiple || typeof maxFiles !== 'number' || items.length < maxFiles
    const hasItems = items.length > 0
    const replaceButtonLabel = multiple ? (hasItems ? 'Изменить выбор' : 'Выбрать фото') : 'Заменить'
    const description = multiple
        ? 'Можно выбрать несколько фото из галереи или загрузить новые'
        : 'По умолчанию будет использован первый снимок из галереи товара'

    const modalHeading = multiple ? 'Выберите фотографии' : 'Выберите фотографию'
    const modalSubtitle = multiple
        ? 'Отметьте нужные фото из галереи или загрузите новые'
        : 'Выберите фото из галереи или загрузите новое'

    return (
        <div className={cn('flex flex-col gap-3 w-full', className)}>
            <div className='flex flex-row gap-[1rem]'>
                {!multiple && (
                    <div className='min-w-[100px] h-[100px] rounded-[8px] overflow-hidden bg-[rgba(255,255,255,0.05)]'>
                        {items[0] && (
                            <img
                                src={items[0].previewUrl}
                                alt='Выбранная фотография'
                                className='max-w-[100px] max-h-[100px] object-cover aspect-square'
                            />
                        )}
                    </div>
                )}
                <div className='flex items-center justify-between gap-3 w-full'>
                    <div className='flex justify-center items-center'>
                        <div className='flex flex-col gap-[4px]'>
                            {title && <h4 className='text-[16px]/[22px] text-[var(--pixorion-light-gray)]'>{title}</h4>}
                            <p className='text-[12px]/[16px] text-[var(--pixorion-gray-2)]'>{description}</p>

                            <div className='flex gap-2 mt-2'>
                                <Button
                                    type='button'
                                    size='md'
                                    variant='outline'
                                    onClick={handleOpenGallery}
                                    disabled={isSelecting || disabled}
                                    aria-label={replaceButtonLabel}
                                >
                                    {replaceButtonLabel}
                                </Button>
                                {multiple && hasItems && (
                                    <Button
                                        type='button'
                                        size='md'
                                        variant='outline'
                                        onClick={clearAll}
                                        disabled={isSelecting || disabled}
                                        aria-label='Очистить выбор'
                                    >
                                        Очистить
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {multiple && hasItems && (
                <div className='flex flex-wrap gap-[8px]' role='list' aria-label='Выбранные фотографии'>
                    {items.map((item, index) => (
                        <div
                            key={item.key}
                            role='listitem'
                            className={cn(
                                'relative w-[80px] h-[100px] rounded-[8px] overflow-hidden border-2',
                                'border-[#be96ff] bg-[rgba(255,255,255,0.05)]'
                            )}
                        >
                            <img src={item.previewUrl} alt='' className='w-full h-full object-cover' />
                            <span
                                className={cn(
                                    'absolute top-[4px] left-[4px] min-w-[18px] h-[18px] px-1 rounded-full',
                                    'bg-[#be96ff] text-[10px]/[18px] text-white text-center font-medium'
                                )}
                                aria-hidden
                            >
                                {index + 1}
                            </span>
                            <button
                                type='button'
                                onClick={() => removeFile(item.file)}
                                disabled={isSelecting || disabled}
                                aria-label='Удалить изображение'
                                className={cn(
                                    'absolute top-[4px] right-[4px] w-[20px] h-[20px] rounded-full',
                                    'bg-black/60 text-white flex items-center justify-center',
                                    'hover:bg-black/80 disabled:opacity-60 disabled:cursor-not-allowed'
                                )}
                            >
                                <Icon name='cross' className='w-3 h-3 text-white' />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <Modal id={modalId} modalClassName='max-w-[720px] min-w-[60%]'>
                <div className='flex flex-col gap-[16px]'>
                    <div className='flex flex-col gap-[4px]'>
                        <h3 className='text-[18px]/[24px] font-semibold text-[var(--pixorion-light-gray)]'>
                            {modalHeading}
                        </h3>
                        <p className='text-[13px]/[18px] text-[var(--pixorion-gray-2)]'>{modalSubtitle}</p>
                    </div>

                    <input
                        ref={uploadInputRef}
                        type='file'
                        accept='image/*'
                        multiple={multiple}
                        className='hidden'
                        onChange={handleUploadInputChange}
                        aria-label={multiple ? 'Загрузить новые изображения' : 'Загрузить новое изображение'}
                    />

                    <div
                        className='flex flex-wrap gap-[12px] max-h-[60vh] overflow-y-auto pr-1'
                        role='listbox'
                        aria-label='Галерея фотографий'
                        aria-multiselectable={multiple}
                    >
                        {canAddMore && (
                            <div
                                role='button'
                                tabIndex={0}
                                aria-label={multiple ? 'Загрузите новые изображения' : 'Загрузите новое изображение'}
                                onClick={handleUploadAreaClick}
                                onKeyDown={handleUploadAreaKeyDown}
                                onDragOver={handleUploadAreaDragOver}
                                onDragLeave={handleUploadAreaDragLeave}
                                onDrop={handleUploadAreaDrop}
                                className={cn(
                                    'w-[108px] h-[140px] rounded-[8px] overflow-hidden border-2 border-dashed',
                                    'flex flex-col items-center justify-center gap-[6px] text-center',
                                    'text-[12px]/[16px] px-2 transition-colors cursor-pointer',
                                    isDragOverUploadArea
                                        ? 'border-[#be96ff] bg-[#be96ff]/10 text-[var(--pixorion-light-gray)]'
                                        : 'border-[var(--pixorion-gray-2)]/40 text-[var(--pixorion-gray-2)] hover:border-[#be96ff]/60',
                                    isSelecting && 'pointer-events-none opacity-60'
                                )}
                            >
                                <Icon name='gallery-import' className='w-6 h-6' />
                                <span>{multiple ? 'Загрузить' : 'Загрузить новое'}</span>
                            </div>
                        )}
                        {uploadedItems.map(item => (
                            <button
                                key={item.key}
                                type='button'
                                onClick={() => removeFile(item.file)}
                                disabled={isSelecting || disabled}
                                role='option'
                                aria-selected
                                aria-label='Убрать загруженное изображение'
                                className={cn(
                                    'relative w-[108px] h-[140px] rounded-[8px] overflow-hidden border-2 transition-colors',
                                    'border-[#be96ff] disabled:opacity-60 disabled:cursor-not-allowed'
                                )}
                            >
                                <img src={item.previewUrl} alt='' className='w-full h-full object-cover' />
                                <span
                                    className={cn(
                                        'absolute top-[6px] left-[6px] px-[6px] h-[18px] rounded-full',
                                        'bg-black/60 text-[10px]/[18px] text-white font-medium'
                                    )}
                                    aria-hidden
                                >
                                    Загружено
                                </span>
                                <span className='absolute inset-0 flex items-end justify-end p-[6px] bg-[#be96ff]/20'>
                                    <span className='w-6 h-6 rounded-full bg-[#be96ff] flex items-center justify-center'>
                                        <Icon name='check' className='w-3 h-3 text-white' />
                                    </span>
                                </span>
                            </button>
                        ))}
                        {galleryImages.map(galleryImage => {
                            const isSelected = selectedGalleryUrls.has(galleryImage)
                            const isAddBlocked = !isSelected && !canAddMore
                            return (
                                <button
                                    key={`gallery:${galleryImage}`}
                                    type='button'
                                    onClick={() => void toggleGalleryImage(galleryImage)}
                                    disabled={isSelecting || disabled || isAddBlocked}
                                    role='option'
                                    aria-selected={isSelected}
                                    className={cn(
                                        'relative w-[108px] h-[140px] rounded-[8px] overflow-hidden border-2 transition-colors',
                                        isSelected
                                            ? 'border-[#be96ff]'
                                            : 'border-transparent hover:border-[#be96ff]/40',
                                        'disabled:opacity-60 disabled:cursor-not-allowed'
                                    )}
                                    aria-label={isSelected ? 'Убрать фотографию' : 'Выбрать фотографию'}
                                >
                                    <img src={galleryImage} alt='' className='w-full h-full object-cover' />
                                    {isSelected && (
                                        <span
                                            className={cn(
                                                'absolute inset-0 flex items-end justify-end p-[6px]',
                                                'bg-[#be96ff]/20'
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'w-6 h-6 rounded-full bg-[#be96ff]',
                                                    'flex items-center justify-center'
                                                )}
                                            >
                                                <Icon name='check' className='w-3 h-3 text-white' />
                                            </span>
                                        </span>
                                    )}
                                </button>
                            )
                        })}
                    </div>

                    {galleryImages.length === 0 && uploadedItems.length === 0 && (
                        <p className='text-sm text-[#aba7af]'>{emptyText}</p>
                    )}

                    <div className='flex justify-end gap-2 pt-[8px] border-t border-white/5'>
                        {multiple ? (
                            <Button type='button' size='md' variant='filled' onClick={closeModal} aria-label='Готово'>
                                Готово
                            </Button>
                        ) : (
                            <Button type='button' size='md' variant='outline' onClick={closeModal} aria-label='Закрыть'>
                                Закрыть
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    )
}
